/* eslint-disable max-lines */
/* eslint-disable max-lines-per-function */
import { useEffect, useRef, useState, useCallback } from 'react';
import dayjs from 'dayjs';
import './MilestoneDetails.scss';
import { TableUtility } from '@utility/dynamic-table';
import 'primereact/resources/themes/saga-blue/theme.css';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';
import { useAuth } from '../../../Hooks/useAuth';
import { MODULE_NAMES } from '../../../Constants/modules';
import PropTypes from 'prop-types';
import {
  appendServiceNameToURL,
  appendServicePortToURL,
} from '../../../Api/resources/serviceURLs';
import { serviceName } from '../../../Api/resources/serviceConstants';
import { MILESTONE_DETAILS } from '../../../Api/resources/endPoint';
import {
  getExportFileName,
  getExportFileNameWithDate,
} from '../../Delivery/utils';
import MilestoneFormDialog from './MilestoneFormDialog';
import InvoiceFormDialog from './InvoiceFormDialog';
import CollectionFormDialog from './CollectionFormDialog';
/* -------------------------------------------------------------------------- */
/*  Base URL                                                                   */
/* -------------------------------------------------------------------------- */

const fetchBaseURL = () => {
  const serviceURL = appendServiceNameToURL(serviceName.PROJECT_MS);
  const servicePort = appendServicePortToURL(serviceName.PROJECT_MS);
  const baseUrl = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');

  if (process.env.NODE_ENV === 'production') {
    return `${baseUrl}${serviceURL}/api${MILESTONE_DETAILS}`;
  }

  return `${baseUrl}:${servicePort}${serviceURL}/api${MILESTONE_DETAILS}`;
};

const baseURL = fetchBaseURL();

/* -------------------------------------------------------------------------- */
/*  Cell click handler                                                         */
/* -------------------------------------------------------------------------- */

/* -------------------------------------------------------------------------- */
/*  Due date required validation                                               */
/* -------------------------------------------------------------------------- */

const applyDueDateRequiredValidation = (menuItems = []) =>
  Array.isArray(menuItems)
    ? menuItems.map((menuItem) => ({
      ...menuItem,
      items: Array.isArray(menuItem?.items)
        ? menuItem.items.map((item) => {
          if (item?.type !== 'filter') return item;
          if (
            item?.field === 'due_date'
            || item?.field === 'milestone_date.due_date'
          ) {
            return {
              ...item,
              tableUtilityConfig: {
                ...(item?.tableUtilityConfig || {}),
                requiredOnMainApply: true,
                requiredOnMainApplyWhenFieldsActive: [
                  'project_name',
                  'project_vertical',
                  'project_manager',
                  'sr_manager',
                  'delivery_manager',
                  'milestone_status',
                  'invoice_status_name',
                  'project_status',
                  'po_number',
                  'milestone_status_name',
                  'customer_name',
                  'milestone_date.replanned_due_date',
                  'invoice_date.invoice_date',
                  'invoice_date.revenue_date',
                ],
                requiredValidationMessage: 'Due Date is mandatory.',
              },
            };
          }
          return item;
        })
        : menuItem?.items,
    }))
    : [];

/* -------------------------------------------------------------------------- */
/*  Date formatting & column transform helpers                                 */
/* -------------------------------------------------------------------------- */

const AMOUNT_FIELD_REGEX = /amount|amt|creditperiod|contractvaluepercent|datevariance/i;
const HIDDEN_COLUMN_FIELDS = new Set([
  'collectionId',
  'amountToCollect',
  'collectionDueDate',
  'collectionProjectionDate',
  'collectionDate',
  'collectedAmount',
  'collectionComment',
  'collectionRemarks',
  'project_manager_name',
  'senior_manager_name',
  'delivery_manager_name',
]);

const formatDateValue = (value) => {
  if (!value || typeof value !== 'string') return value;
  const trimmed = value.trim();
  if (!trimmed || trimmed === '-') return value;
  const slashMatch = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(trimmed);
  if (slashMatch) {
    const parsed = dayjs(`${slashMatch[3]}-${slashMatch[2]}-${slashMatch[1]}`);
    return parsed.isValid() ? parsed.format('DD-MMM-YYYY') : value;
  }
  const parsed = dayjs(trimmed);
  return parsed.isValid() ? parsed.format('DD-MMM-YYYY') : value;
};

const applyDateFormatting = (rows = [], dateFields = []) => {
  if (dateFields.length === 0) return rows;
  return rows.map((row) => {
    if (!row || typeof row !== 'object') return row;
    const updated = { ...row };
    dateFields.forEach((field) => {
      if (updated[field] != null) {
        updated[field] = formatDateValue(String(updated[field]));
      }
    });
    if (Array.isArray(updated.children)) {
      updated.children = applyDateFormatting(updated.children, dateFields);
    }
    return updated;
  });
};

/* -------------------------------------------------------------------------- */
/*  Filtered API response handler                                              */
/* -------------------------------------------------------------------------- */

const handleFilteredApiResponse = (response) => {
  if (!response) return;

  const normalizeStatus = (statusValue) =>
    String(statusValue || '')
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '');

  let rawTableConfiguration = [];

  if (Array.isArray(response.data?.tableConfiguration)) {
    rawTableConfiguration = response.data.tableConfiguration;
  } else if (Array.isArray(response.data?.data?.tableConfiguration)) {
    rawTableConfiguration = response.data.data.tableConfiguration;
  }

  const ensureColumn = (field, header) => {
    if (!rawTableConfiguration.some((col) => col.field === field)) {
      rawTableConfiguration.push({ field, header, sortable: true, filter: true, width: 110 });
    }
  };

  ensureColumn('country', 'Country');
  ensureColumn('project_manager', 'Project Manager');
  ensureColumn('senior_manager', 'Senior Manager');
  ensureColumn('delivery_manager', 'Delivery Manager');

  rawTableConfiguration = rawTableConfiguration.filter(
    (col) => !HIDDEN_COLUMN_FIELDS.has(col?.field),
  );

  const statusColumnConfig = rawTableConfiguration.find(
    (column) =>
      normalizeStatus(column?.field) === 'status'
      && column?.style
      && typeof column.style === 'object',
  );

  const milestoneStatusColumnConfig = rawTableConfiguration.find(
    (col) => col?.field === 'milestoneStatus' && col?.style && typeof col.style === 'object',
  );

  const invoiceStatusColumnConfig = rawTableConfiguration.find(
    (col) => col?.field === 'invoiceStatus' && col?.style && typeof col.style === 'object',
  );

  const buildStyleMap = (columnConfig) =>
    Object.entries(columnConfig?.style || {}).reduce(
      (acc, [statusKey, styleValue]) => {
        if (!styleValue || typeof styleValue !== 'object') return acc;
        const normalizedStatus = normalizeStatus(statusKey);
        const textColor = styleValue.textColor || styleValue.textColour;
        const boxColor = styleValue.boxColor || styleValue.backgroundColor;
        if (normalizedStatus) {
          acc[normalizedStatus] = { textColor, boxColor };
        }
        return acc;
      },
      {},
    );

  const statusStyleMap = buildStyleMap(statusColumnConfig);
  const milestoneStatusStyleMap = buildStyleMap(milestoneStatusColumnConfig);
  const invoiceStatusStyleMap = buildStyleMap(invoiceStatusColumnConfig);

  const applyFieldStyle = (row, existingTemplate, field, styleMap) => {
    const style = styleMap[normalizeStatus(row[field])];
    if (!style?.textColor && !style?.boxColor) return existingTemplate;
    return [
      ...existingTemplate.filter((entry) => entry.field !== field),
      { field, ...(style.textColor ? { textColor: style.textColor } : {}), fontWeight: '600' },
    ];
  };

  const addStatusStyles = (rows = []) =>
    rows.map((row) => {
      if (!row || typeof row !== 'object') return row;
      let updatedRow = { ...row };

      let template = updatedRow.rowConfiguration?.template || [];
      template = applyFieldStyle(updatedRow, template, 'status', statusStyleMap);
      template = applyFieldStyle(updatedRow, template, 'milestoneStatus', milestoneStatusStyleMap);
      template = applyFieldStyle(updatedRow, template, 'invoiceStatus', invoiceStatusStyleMap);

      const milestoneIdField = ['milestone_id', 'milestoneId', 'mile_id'].find(
        (field) => Object.prototype.hasOwnProperty.call(updatedRow, field),
      );

      if (milestoneIdField) {
        template = [
          ...template.filter((entry) => entry.field !== milestoneIdField),
          { field: milestoneIdField, fontWeight: '700' },
        ];
      }

      if (template !== (updatedRow.rowConfiguration?.template || [])) {
        updatedRow = {
          ...updatedRow,
          rowConfiguration: {
            ...(updatedRow.rowConfiguration || {}),
            template,
          },
        };
      }

      if (Array.isArray(row.children)) {
        updatedRow.children = addStatusStyles(row.children);
      }

      return updatedRow;
    });

  const dateFields = rawTableConfiguration
    .filter((col) => col?.type === 'date' && col?.field)
    .map((col) => col.field);

  const rawTableData
    = response.data?.tableData || response.data?.data?.tableData || [];
  const tableDataWithStatusStyles = addStatusStyles(rawTableData);
  const tableData = applyDateFormatting(tableDataWithStatusStyles, dateFields);

  const processedTableConfiguration = rawTableConfiguration.map((column) => {
    const fieldStr = String(column?.field || '');
    const isAmount = AMOUNT_FIELD_REGEX.test(fieldStr);
    const isFirstTwoCols = column?.order <= 1;
    const widthNum = Number(column?.width);
    const compactWidth = Number.isFinite(widthNum)
      ? Math.max(75, Math.round(widthNum * 0.72))
      : column?.width;
    return {
      ...column,
      width: compactWidth,
      defaultPinned: isFirstTwoCols,
      isPinned: isFirstTwoCols,
      pinnable: false,
      ...(isAmount ? { align: 'right', headerAlign: 'right' } : {}),
      menuItems: applyDueDateRequiredValidation(
        Array.isArray(column?.menuItems) ? column.menuItems : [],
      ),
    };
  });

  response.data = {
    ...response.data,
    tableConfiguration: processedTableConfiguration,
    tableData,
    data: {
      ...(response.data?.data || {}),
      tableConfiguration: processedTableConfiguration,
      tableData,
    },
  };

  const pagination
    = response.data?.pagination
    || response.data?.paginationConfig
    || response.data?.data?.pagination
    || response.data?.data?.paginationConfig;

  response.meta = {
    ...(response.meta || {}),
    page: pagination?.page ?? response.meta?.page ?? 1,
    limit: pagination?.limit ?? response.meta?.limit ?? 10,
    total:
      pagination?.total
      ?? pagination?.count
      ?? response.meta?.total
      ?? response.data?.tableData?.length
      ?? response.data?.data?.tableData?.length
      ?? 0,
  };
};

// Only sync total: page/limit feed TableUtility's fetch key, so syncing them re-triggers the API.
const syncPaginationFromResponse = (setPagination, response) => {
  const total = response?.meta?.total;
  setPagination((prev) => (
    total == null || total === prev.total ? prev : { ...prev, total }
  ));
};

const resetPaginationToFirstPage = (setPagination) => {
  setPagination((prev) => ({ ...prev, page: 1 }));
};

/* -------------------------------------------------------------------------- */
/*  DOM effects hook                                                           */
/* -------------------------------------------------------------------------- */

const applyMilestoneIconClass = () => {
  document.querySelectorAll('td[style*="border-left: none"]').forEach((td) => {
    const icon = td.querySelector('.pi.pi-angle-right');
    if (icon) icon.classList.add('milestone-details-angle-right-icon');
  });
};

const applyAddButtonTooltip = () => {
  const container = document.querySelector('.milestone-details-table-utility');
  if (!container) return;
  container.querySelectorAll('button').forEach((btn) => {
    const titleAttr = btn.getAttribute('title') || '';
    const ariaLabel = btn.getAttribute('aria-label') || '';
    const textContent = (btn.textContent || '').trim();
    const isAddNew
      = titleAttr === 'Add New'
      || ariaLabel === 'Add New'
      || textContent === 'Add New';
    if (isAddNew) {
      btn.setAttribute('title', 'Add Milestone');
      btn.setAttribute('aria-label', 'Add Milestone');
    }
  });
};

const applyBodyCellHoverTitles = () => {
  document
    .querySelectorAll('.milestone-details-table-utility .p-datatable-tbody > tr > td')
    .forEach((td) => {
      const contentElement
        = td.querySelector('.cell-wrapper .ellipses')
        || td.querySelector('.cell-wrapper > span')
        || td.querySelector('span')
        || td.querySelector('div')
        || td;
      const fullText = (contentElement.textContent || '').trim();
      const isTruncated = contentElement.scrollWidth > contentElement.clientWidth;
      if (fullText && isTruncated) {
        td.setAttribute('title', fullText);
        contentElement.setAttribute('title', fullText);
      } else {
        td.removeAttribute('title');
        if (contentElement !== td) contentElement.removeAttribute('title');
      }
    });
};

const useMilestoneTableEffects = ({
  containerRef,
  resetToCurrentDate,
  setTableScrollHeight,
  getRowCount,
}) => {
  useEffect(() => {
    const rootNode = containerRef.current;

    const handleResetClick = (event) => {
      if (!rootNode) return;
      const resetButton = event.target.closest('button');
      if (!resetButton || !rootNode.contains(resetButton)) return;
      const clickedResetIcon
        = Boolean(event.target.closest('.pi-refresh'))
        || Boolean(resetButton.querySelector('.pi-refresh'));
      if (clickedResetIcon) resetToCurrentDate();
    };

    rootNode?.addEventListener('click', handleResetClick);

    const timer = setTimeout(() => {
      applyMilestoneIconClass();
      applyBodyCellHoverTitles();
      applyAddButtonTooltip();
    }, 500);
    const tableContainer = document.querySelector('.milestone-details-table-utility');
    const observer = new MutationObserver(() => {
      applyBodyCellHoverTitles();
    });
    if (tableContainer) {
      observer.observe(tableContainer, { childList: true, subtree: true, characterData: true });
    }

    // PrimeReact renders tooltips as `.p-tooltip` overlays appended to <body>.
    // The toolbar Add button uses tooltip="Add New" — intercept when it appears
    // and replace the text with "Add Milestone".
    const tooltipObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (node.nodeType === 1 && node.classList?.contains('p-tooltip')) {
            const textEl = node.querySelector('.p-tooltip-text');
            if (textEl && textEl.textContent.trim() === 'Add New') {
              textEl.textContent = 'Add Milestone';
            }
          }
        }
      }
    });
    tooltipObserver.observe(document.body, { childList: true });

    const handleResize = () => {
      const rowCount = Number(getRowCount?.()) || 0;
      const contentHeight = Math.max(220, (rowCount * 34) + 118);
      const maxHeight = Math.floor(window.innerHeight * 0.58);
      setTableScrollHeight(`${Math.min(contentHeight, maxHeight)}px`);
    };
    handleResize();
    window.addEventListener('resize', handleResize);

    return () => {
      rootNode?.removeEventListener('click', handleResetClick);
      clearTimeout(timer);
      observer.disconnect();
      tooltipObserver.disconnect();
      window.removeEventListener('resize', handleResize);
    };
  }, [containerRef, resetToCurrentDate, setTableScrollHeight, getRowCount]);
};

/* -------------------------------------------------------------------------- */
/*  MilestoneTable component                                                   */
/* -------------------------------------------------------------------------- */

/* -------------------------------------------------------------------------- */
/*  Transform actions { edit, add } booleans → renderable JSX buttons         */
/* -------------------------------------------------------------------------- */

// Recursively flatten hierarchical children into a single-level array,
// matching PHP milestone.php which renders all rows in one flat <table>.
const flattenHierarchicalData = (rows) => {
  const flat = [];
  for (const row of rows) {
    const { children, ...rowData } = row;
    flat.push({ ...rowData, children: [] });
    if (Array.isArray(children) && children.length > 0) {
      flat.push(...flattenHierarchicalData(children));
    }
  }
  return flat;
};

const EDIT_LABELS = {
  milestone: 'Edit Milestone',
  invoice: 'Edit Invoice',
  collection: 'Edit Collection',
};

const ADD_LABELS = {
  milestone: 'Add Invoice',
  invoice: 'Add Collection',
};

const applyActionButtons = (
  rows = [],
  onEditClick,
  onAddClick,
  { canUpdate = false, canCreate = false, isSuperAdmin = false } = {},
) =>
  rows.map((row) => {
    if (!row || typeof row !== 'object') return row;
    const actionData = row.actions;
    if (!actionData || typeof actionData !== 'object') return row;

    const rowType = row.row_type || '';
    const editLabel = EDIT_LABELS[rowType] || 'Edit';
    const addLabel = ADD_LABELS[rowType] || 'Add';

    // Always render both slots so Edit and Add columns stay vertically aligned
    // across all rows. Absent buttons are rendered as invisible placeholders.
    const editBtn = (rowType !== 'collection' && actionData.edit && (canUpdate || isSuperAdmin))
      ? (
        <button
          key="edit"
          type="button"
          className="ms-action-btn ms-action-btn--edit"
          title={editLabel}
          onClick={(ev) => {
            ev.stopPropagation();
            onEditClick?.(row);
          }}
        >
          <i className="pi pi-pencil" />
        </button>
      )
      : <span key="edit" className="ms-action-btn-placeholder" />;

    const addBtn = (rowType !== 'invoice' && actionData.add && (canCreate || isSuperAdmin))
      ? (
        <button
          key="add"
          type="button"
          className="ms-action-btn ms-action-btn--add"
          title={addLabel}
          onClick={(ev) => {
            ev.stopPropagation();
            onAddClick?.(row);
          }}
        >
          <i className="pi pi-plus" />
        </button>
      )
      : <span key="add" className="ms-action-btn-placeholder" />;

    const actionCell = (
      <div className="ms-action-btns">{editBtn}{addBtn}</div>
    );

    const updatedRow = { ...row, actions: actionCell };
    if (Array.isArray(row.children)) {
      updatedRow.children = applyActionButtons(
        row.children,
        onEditClick,
        onAddClick,
        {
          canUpdate,
          canCreate,
          isSuperAdmin,
        },
      );
    }
    return updatedRow;
  });

/* -------------------------------------------------------------------------- */
/*  MilestoneTable component                                                   */
/* -------------------------------------------------------------------------- */

// Accept onFilteredApiResponse prop for parent to track project selection
const MilestoneTable = ({ projectId, onFilteredApiResponse, initialDialog }) => {
  const { userProfile } = useAuth();
  const containerRef = useRef(null);
  const hasAutoOpenedDialogRef = useRef(false);

  const permissions = userProfile?.permissions || {};
  const getPermissionActions = (moduleName) => {
    const actions = permissions?.[moduleName];
    return Array.isArray(actions) ? actions : [];
  };
  const hasPermissionAction = (moduleName, action) =>
    getPermissionActions(moduleName).includes(action);
  const isSuperAdmin = Boolean(userProfile?.superAdmin || userProfile?.super_admin);

  const canCreateMilestone = isSuperAdmin || hasPermissionAction(MODULE_NAMES.FINANCE_MILESTONE, 'create');
  const canUpdateMilestone = isSuperAdmin || hasPermissionAction(MODULE_NAMES.FINANCE_MILESTONE, 'update');

  const [tableScrollHeight, setTableScrollHeight] = useState('400px');
  const [toDate, setToDate] = useState(dayjs());
  const [pagination, setPagination] = useState({ page: 1, limit: 10, total: 0 });
  const [refreshKey, setRefreshKey] = useState(0);
  const [dialog, setDialog] = useState({ type: null, row: null });
  const [toast, setToast] = useState({ visible: false, message: '' });
  const latestRowCountRef = useRef(0);

  // Always scoped to the selected project
  const tableBaseURL = `${baseURL}/projects/${projectId}`;

  const openDialog = useCallback((type, row = null) => {
    setDialog({ type, row });
  }, []);

  const closeDialog = useCallback(() => {
    setDialog({ type: null, row: null });
  }, []);

  const handleSuccess = useCallback((message) => {
    setToast({ visible: true, message });
    setTimeout(() => {
      setToast({ visible: false, message: '' });
    }, 3000);
    setRefreshKey((prev) => prev + 1);
  }, []);

  const onEditClick = useCallback((row) => {
    switch (row.row_type) {
    case 'milestone': openDialog('editMilestone', row); break;
    case 'invoice': openDialog('editInvoice', row); break;
    case 'collection': openDialog('editCollection', row); break;
    default: break;
    }
  }, [openDialog]);

  const onAddClick = useCallback((row) => {
    switch (row.row_type) {
    case 'milestone': openDialog('addInvoice', row); break;
    case 'invoice': openDialog('addCollection', row); break;
    default: break;
    }
  }, [openDialog]);

  const handleFilteredApiResponseWithState = useCallback((response) => {
    handleFilteredApiResponse(response);
    syncPaginationFromResponse(setPagination, response);

    // Transform actions data into renderable JSX buttons, then flatten the
    // hierarchy so the flat Table renders all milestone/invoice/collection rows
    // in sequence — identical to how PHP milestone.php outputs flat <tr> rows.
    if (Array.isArray(response?.data?.tableData)) {
      const withButtons = applyActionButtons(
        response.data.tableData,
        onEditClick,
        onAddClick,
        {
          canUpdate: canUpdateMilestone,
          canCreate: canCreateMilestone,
          isSuperAdmin,
        },
      );
      const flattened = flattenHierarchicalData(withButtons)
        .filter((row) => row.row_type !== 'collection');
      latestRowCountRef.current = flattened.length;
      const contentHeight = Math.max(220, (flattened.length * 34) + 118);
      const maxHeight = Math.floor(window.innerHeight * 0.58);
      setTableScrollHeight(`${Math.min(contentHeight, maxHeight)}px`);
      response.data.tableData = flattened;
      if (response.data?.data) {
        response.data.data.tableData = flattened;
      }
      // Update meta so the flat Table paginates across the full flattened count
      if (response.meta) {
        response.meta.total = flattened.length;
        response.meta.limit = flattened.length;
      }

      if (
        !hasAutoOpenedDialogRef.current
        && initialDialog?.editType
      ) {
        const matchedRow = flattened.find((row) => {
          if (!row || typeof row !== 'object') return false;

          switch (String(initialDialog.editType).toLowerCase()) {
          case 'milestone':
          case 'addinvoice':
            return String(row?.milestoneId ?? row?.milestone_id ?? '') === String(initialDialog.milestoneId || '');
          case 'invoice':
            return String(row?.invoiceId ?? row?.invoice_id ?? '') === String(initialDialog.invoiceId || '');
          case 'collection':
            return String(row?.collectionId ?? row?.collection_id ?? '') === String(initialDialog.collectionId || '');
          default:
            return false;
          }
        }) || null;

        if (matchedRow) {
          hasAutoOpenedDialogRef.current = true;

          switch (String(initialDialog.editType).toLowerCase()) {
          case 'milestone':
            openDialog('editMilestone', matchedRow);
            break;
          case 'addinvoice':
            openDialog('addInvoice', matchedRow);
            break;
          case 'invoice':
            openDialog('editInvoice', matchedRow);
            break;
          case 'collection':
            openDialog('editCollection', matchedRow);
            break;
          default:
            break;
          }
        }
      }
    }
    // Notify parent of filter/project selection
    if (typeof onFilteredApiResponse === 'function') {
      onFilteredApiResponse(response);
    }
  }, [initialDialog, onEditClick, onAddClick, onFilteredApiResponse, openDialog]);

  const handlePaginationChange = (newPagination) => {
    setPagination(newPagination);
  };

  const formattedToDate = toDate ? dayjs(toDate).format('DD-MM-YYYY') : '';

  const resetToCurrentDate = () => {
    setToDate(dayjs());
    resetPaginationToFirstPage(setPagination);
  };

  const getCurrentRowCount = useCallback(() => latestRowCountRef.current, []);

  useMilestoneTableEffects({
    containerRef,
    resetToCurrentDate,
    setTableScrollHeight,
    getRowCount: getCurrentRowCount,
  });

  const configuration = {
    generalSettings: {
      enableApiFiltering: true,
      baseURL: tableBaseURL,

      userInfo: {
        emailId: userProfile.email,
        permissions: userProfile.permissions,
        roleDetails: userProfile.roleDetails,
        employeeId: userProfile.employeeId,
        globalColors: userProfile?.siteConfiguration?.global_colors,
      },

      apiEndpoints: {
        getAllViews: { endpoint: '', enabled: false }, // Disable views
        getFilteredData: { endpoint: '', enabled: true },
        saveOrUpdateView: { endpoint: 'view', enabled: true },
        deleteView: { endpoint: 'view-delete', enabled: false },
        download: {
          endpoint: 'export',
          enabled: true,
          filename: () => getExportFileName('MILESTONE DETAILS'),
          sheetName: () => getExportFileNameWithDate('MILESTONE DETAILS'),
        },
      },

      handler: {
        handleFilteredApiResponse: handleFilteredApiResponseWithState,
      },

      additionalPayloadData: {
        toDate: formattedToDate,
      },

      headers: {
        token: userProfile.access_token,
      },

      showToolBar: true,
      showPagination: false,
      showTieredMenu: true,

      toolBarSettings: {
        showAddNew: Boolean(canCreateMilestone),
        showDownloadExcel: false,
        showColumnCustomize: false,
        showHelp: false,
        showSearch: false,
        showToolBarHeading: true,
        toolBarHeading: 'Milestone Details',
        showSaveView: false,
        showReset: false,
        showExpandCollapse: false,
        showFilterOption: true, // Enable filter icon/panel
      },

      handlers: {
        handleAddIconClick: () => {
          openDialog('addMilestone');
        },
      },
    },

    showFilterOption: true,

    tableSettings: {
      size: 'small',
      pageSize: 10,
      loaderSkeleton: {
        rows: 10,
        bodyCells: 2,
        columns: 5,
      },
      paginationConfig: {
        ...pagination,
        onPaginationChange: handlePaginationChange,
        rowsPerPageOptions: [10, 20, 30, 40],
      },
      showExpandCollapse: false,
      tableScrollHeight,
      maxFrozenColumns: {
        desktop: 2,
        tablet: 2,
        mobile: 1,
      },
    },
  };

  return (
    <div className="milestone-details-outer-layer" ref={containerRef}>
      <div className="milestone-details">
        <div key={refreshKey} className="milestone-details-table-utility">
          <TableUtility configuration={configuration} />
        </div>
      </div>

      {toast.visible && (
        <div className="ms-toast">{toast.message}</div>
      )}

      <MilestoneFormDialog
        visible={dialog.type === 'addMilestone' || dialog.type === 'editMilestone'}
        onHide={closeDialog}
        mode={dialog.type === 'editMilestone' ? 'edit' : 'add'}
        projectId={projectId}
        rowData={dialog.row}
        token={userProfile.access_token}
        onSuccess={handleSuccess}
      />

      <InvoiceFormDialog
        visible={dialog.type === 'addInvoice' || dialog.type === 'editInvoice'}
        onHide={closeDialog}
        mode={dialog.type === 'editInvoice' ? 'edit' : 'add'}
        milestoneRow={dialog.type === 'addInvoice' ? dialog.row : null}
        rowData={dialog.type === 'editInvoice' ? dialog.row : null}
        projectId={projectId}
        token={userProfile.access_token}
        onSuccess={handleSuccess}
      />

      <CollectionFormDialog
        visible={dialog.type === 'addCollection' || dialog.type === 'editCollection'}
        onHide={closeDialog}
        mode={dialog.type === 'editCollection' ? 'edit' : 'add'}
        invoiceRow={dialog.type === 'addCollection' ? dialog.row : null}
        rowData={dialog.type === 'editCollection' ? dialog.row : null}
        token={userProfile.access_token}
        onSuccess={handleSuccess}
      />

    </div>
  );
};

export default MilestoneTable;

MilestoneTable.propTypes = {
  projectId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  onFilteredApiResponse: PropTypes.func,
  initialDialog: PropTypes.shape({
    editType: PropTypes.string,
    milestoneId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    invoiceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    collectionId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
};

MilestoneTable.defaultProps = {
  onFilteredApiResponse: undefined,
  initialDialog: null,
};

